import { useState, useEffect } from "react";
import { creditNotesApi, invoicesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Receipt, Loader2, CheckCircle, MoreVertical, FileText, FileCode, Archive, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditNotesPage = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    invoice_id: "",
    reason: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [notesRes, invoicesRes] = await Promise.all([
        creditNotesApi.list(),
        invoicesApi.list({ status: "issued" })
      ]);
      setCreditNotes(notesRes.data);
      setInvoices(invoicesRes.data.filter(inv => inv.status === "issued"));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.invoice_id) {
      toast.error("Selecciona una factura");
      return;
    }

    setSubmitting(true);

    try {
      await creditNotesApi.create(formData);
      toast.success("Nota de crédito creada");
      setDialogOpen(false);
      setFormData({ invoice_id: "", reason: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear nota de crédito");
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssue = async (noteId) => {
    try {
      await creditNotesApi.issue(noteId);
      toast.success("Nota de crédito timbrada");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al timbrar");
    }
  };

  const handleViewPdf = (noteId) => {
    window.open(`${API}/credit-notes/${noteId}/pdf`, "_blank");
  };

  const handleViewXml = (noteId) => {
    window.open(`${API}/credit-notes/${noteId}/xml`, "_blank");
  };

  const handleDownloadZip = async (note) => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(
        `${API}/credit-notes/${note.id}/download-zip`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nota_credito_${note.series}-${note.folio}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivos descargados");
    } catch (error) {
      toast.error("Error al descargar archivos");
    }
  };

  const handleResend = async (noteId) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/credit-notes/${noteId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Nota de crédito reenviada al cliente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reenviar");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-MX');
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "badge-draft",
      issued: "badge-issued"
    };
    const labels = {
      draft: "Borrador",
      issued: "Timbrada"
    };
    return <span className={badges[status]}>{labels[status]}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Notas de Crédito</h1>
          <p className="text-slate-600">Administra las notas de crédito de tus facturas</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary" data-testid="new-credit-note-btn">
              <Plus className="w-5 h-5 mr-2" />
              Nueva Nota de Crédito
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear Nota de Crédito</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Factura Relacionada</Label>
                <Select 
                  value={formData.invoice_id} 
                  onValueChange={(value) => setFormData({ ...formData, invoice_id: value })}
                >
                  <SelectTrigger data-testid="invoice-select">
                    <SelectValue placeholder="Seleccionar factura..." />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map((invoice) => (
                      <SelectItem key={invoice.id} value={invoice.id}>
                        {invoice.series}-{invoice.folio} - {invoice.client_name} - {formatCurrency(invoice.total)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Motivo de la Nota de Crédito</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Describe el motivo de la nota de crédito..."
                  className="cia-input"
                  required
                  data-testid="reason-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="cia-btn-primary" disabled={submitting} data-testid="create-note-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Crear Nota"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Credit notes table */}
      <Card className="cia-card">
        <CardContent className="p-0">
          {creditNotes.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No hay notas de crédito</h3>
              <p className="text-slate-500 mb-6">Crea una nota de crédito para una factura existente</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Factura Relacionada</th>
                    <th>Total</th>
                    <th>Motivo</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {creditNotes.map((note) => (
                    <tr key={note.id} data-testid={`credit-note-row-${note.id}`}>
                      <td className="font-medium">{note.series}-{note.folio}</td>
                      <td>{note.invoice_id.slice(0, 8)}...</td>
                      <td>{formatCurrency(note.total)}</td>
                      <td className="max-w-xs truncate">{note.reason}</td>
                      <td>{getStatusBadge(note.status)}</td>
                      <td className="text-slate-500">
                        {formatDate(note.created_at)}
                      </td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {note.status === "draft" && (
                              <DropdownMenuItem onClick={() => handleIssue(note.id)}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Timbrar
                              </DropdownMenuItem>
                            )}
                            
                            {note.status === "issued" && (
                              <>
                                <DropdownMenuItem onClick={() => handleViewPdf(note.id)}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Ver PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewXml(note.id)}>
                                  <FileCode className="w-4 h-4 mr-2" />
                                  Ver XML
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadZip(note)}>
                                  <Archive className="w-4 h-4 mr-2" />
                                  Descargar ZIP
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleResend(note.id)}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Reenviar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreditNotesPage;
